/**
 *
 * SubscriptionCardSharedComponent
 *
 */
import { IconButton, useTheme, Box, Grid } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import * as React from 'react';
import IconText from '../IconText';
import fonts from 'app/assets/styles/fonts';
import { ContractIcon } from 'app/components/Icons/Subscriptionicons';
import { TextWithCopyAction } from '../TextWithCopyAction';
import { PlanType, SubscriptionStatus } from 'types/models';
import LightTooltip from '../LightTooltip';
import { UsageCountChip } from '../UsageCountChip';
import Email from '@mui/icons-material/Email';
import Business from '@mui/icons-material/Business';
import { AsseticChip, ChipColor } from '@assetic/web-components';
import { Link as RouterLink } from 'react-router-dom';
import Account from '@mui/icons-material/AccountCircle';
import { UrlToUser } from 'app/constants';
import { makeThemeStyle } from 'styles/makeThemeStyle';

export const ChipColorMap = (label: SubscriptionStatus): ChipColor => {
  switch (label) {
    case 'active':
      return 'ready';
    case 'in_trial':
      return 'warning';
    case 'suspended':
      return 'error_a';
    case 'non_renewing':
      return 'tertiary';
    case 'not_active':
    default:
      return 'default';
  }
};

interface CardMenuProps {
  subscription_id: string;
}

const useStyles = makeThemeStyle(theme => ({
  menuBtn: {
    padding: 0,
    marginTop: theme.spacing(0.5),
  },
}));

export const SubCardMenuAction = (props: CardMenuProps) => {
  const { subscription_id } = props;
  const classes = useStyles();
  return (
    <IconButton
      className={classes.menuBtn}
      data-testid={`subscription_card_menu_${subscription_id}`}
      onClick={() => alert('Coming Soon')}
      size="large"
    >
      <MoreVertIcon />
    </IconButton>
  );
};

interface CardTitleProps {
  customer_company: string;
}

export const SubCardTitle = (props: CardTitleProps) => {
  const {
    palette: { asseticPrimary },
  } = useTheme();
  const { customer_company } = props;

  return (
    <Box
      color={asseticPrimary.main}
      textOverflow="ellipsis"
      overflow="hidden"
      whiteSpace="nowrap"
    >
      <IconText
        text={customer_company}
        icon={<ContractIcon />}
        textStyle={{
          color: asseticPrimary.main,
          fontSize: fonts.title,
          fontWeight: 'unset',
          lineHeight: '20px',
        }}
        enableTooltip
        iconStyle={{
          height: 20,
          width: 20,
          lineHeight: '20px',
        }}
      />
    </Box>
  );
};
interface SubCardSubHeaderProps {
  subscription_id: string;
}

export const SubCardSubHeader = (props: SubCardSubHeaderProps) => (
  <Box fontSize={fonts.small} whiteSpace="nowrap" pt={0.5}>
    <TextWithCopyAction text={props.subscription_id} />
  </Box>
);

interface CardContentProps {
  plan_type: PlanType;
  data_region_label: string;
  portfolio_usage: number;
  portfolio_limit: number;
  customer_email: string;
  customer_name: string;
}

export const SubCardContent = (props: CardContentProps) => {
  const {
    plan_type,
    data_region_label,
    portfolio_limit,
    portfolio_usage,
    customer_email,
    customer_name,
  } = props;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={5}>
        <Box display="flex" lineHeight={'22px'}>
          <Box color="text.secondary" pr={1}>
            Plan Type
          </Box>
          <Box color="secondary.contrastText" fontWeight={500}>
            {plan_type}
          </Box>
        </Box>
        <Box display="flex" mt={1} lineHeight={'22px'}>
          <Box color="text.secondary" pr={1}>
            Region
          </Box>
          <Box color="secondary.contrastText" fontWeight={500}>
            {data_region_label}
          </Box>
        </Box>
        <Box display="flex" mt={1} alignItems="baseline">
          <Box color="text.secondary" pr={1}>
            Portfolio Usage
          </Box>
          <Box>
            <UsageCountChip
              usage={portfolio_usage || 0}
              limit={portfolio_limit || 0}
              overrideChipStyle={{
                fontSize: fonts.small,
              }}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sm={7}>
        <Box mb={1}>
          <IconText
            icon={<Email />}
            text={
              <LightTooltip placement="top" title={`${customer_email}`}>
                <span>{customer_email}</span>
              </LightTooltip>
            }
          />
        </Box>
        <Box>
          <IconText icon={<Business />} text={customer_name} />
        </Box>
      </Grid>
    </Grid>
  );
};

interface CardActionProps {
  status: SubscriptionStatus;
  owner_id: string;
  owner_email: string;
}

export const SubCardAction = (props: CardActionProps) => {
  const { status, owner_id, owner_email } = props;
  const { spacing } = useTheme();
  return (
    <>
      <AsseticChip label={status} uppercase color={ChipColorMap(status)} />
      {owner_id && (
        <Box
          color="asseticPrimary.main"
          width="65%"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          <IconText
            icon={<Account />}
            iconStyle={{
              marginRight: spacing(0.5),
            }}
            text={
              <RouterLink to={UrlToUser(owner_id).detail}>
                {owner_email}
              </RouterLink>
            }
          />
        </Box>
      )}
    </>
  );
};
